import { memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { PaletaColoresSegmentada } from '@assets/svg/PaletaColoresSegmentada';

import './EditorFloatButton.css';

export interface EditorFloatButtonProps {
    /**
     * Whether the editor is currently minimized (in dock mode).
     * When true, shows additional messaging to indicate active editing session.
     */
    isEditorMinimized?: boolean;
    /**
     * Whether there are unsaved changes in the draft.
     * When true, shows a badge indicator.
     */
    hasUnsavedChanges?: boolean;
    /**
     * Custom click handler. If not provided, navigates to edit mode.
     */
    onClick?: () => void;
    /** Accessible labels for surfaces that reuse the appearance trigger. */
    editLabel?: string;
    restoreLabel?: string;
}

const EditorFloatButtonComponent = ({
    isEditorMinimized = false,
    hasUnsavedChanges = false,
    onClick,
    editLabel = 'Editar tienda',
    restoreLabel = 'Restaurar editor',
}: EditorFloatButtonProps) => {
    const navigate = useNavigate();
    const location = useLocation();

    const handleClick = () => {
        if (onClick) {
            onClick();
            return;
        }
        // Navigate to catalogue edit mode with timestamp to trigger restore if already there
        const timestamp = Date.now();
        const basePath = '/eshop/catalogue';
        const currentPath = location.pathname;

        // If already in catalogue, just update params; otherwise navigate
        if (currentPath.startsWith(basePath)) {
            navigate(`${currentPath}?editTemplate=1&_t=${timestamp}`, { replace: true });
        } else {
            navigate(`${basePath}?editTemplate=1&_t=${timestamp}`);
        }
    };

    const buttonClassName = [
        'float-button',
        'float-button--ghost',
        'editor-float-button',
        isEditorMinimized && 'is-minimized',
        hasUnsavedChanges && 'has-unsaved',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className="editor-float-button-container">
            {isEditorMinimized && (
                <span className="editor-float-button__label">
                    {hasUnsavedChanges ? 'Cambios sin guardar' : 'Editor activo'}
                </span>
            )}
            <button
                type="button"
                className={buttonClassName}
                onClick={handleClick}
                aria-label={
                    isEditorMinimized
                        ? `${restoreLabel}${hasUnsavedChanges ? ' (cambios sin guardar)' : ''}`
                        : editLabel
                }
            >
                <PaletaColoresSegmentada
                    className="editor-float-button__icon"
                    strokeWidth={0.5}
                    strokeColor="white"
                />
                {hasUnsavedChanges && (
                    <span className="editor-float-button__badge" aria-hidden />
                )}
            </button>
        </div>
    );
};

export const EditorFloatButton = memo(EditorFloatButtonComponent);

export default EditorFloatButton;
